/**
 * A person's text-size preference, as a share of the browser's own default.
 *
 * It scales the root font size, so every `rem` size follows it. `html` is
 * never given a `px` size, which would override the browser's own setting.
 *
 * @module utils/text-size
 */

export const TEXT_SIZES = ["default", "large", "larger"] as const;

export type TextSizePreference = (typeof TEXT_SIZES)[number];

/** The root font size each choice sets, as a percentage of the browser default. */
export const TEXT_SIZE_PERCENT: Record<TextSizePreference, number> = {
  default: 100,
  large: 112.5,
  larger: 125,
};

export function isTextSize(value: unknown): value is TextSizePreference {
  return typeof value === "string" && (TEXT_SIZES as readonly string[]).includes(value);
}

/**
 * Scales the root font size to the person's choice. A percentage, so the
 * browser's own setting still counts; the default clears the override.
 */
export function applyTextSize(
  size: TextSizePreference | undefined,
  root: HTMLElement = document.documentElement,
): void {
  if (size === undefined || size === "default") {
    root.style.removeProperty("font-size");
    return;
  }
  root.style.setProperty("font-size", `${TEXT_SIZE_PERCENT[size]}%`);
}
